import { apiFetch } from './client';

export interface MetaRange {
  from: string;
  to: string;
}

export interface MetaOverviewTotals {
  spend: number;
  purchases: number;
  purchaseValue: number;
  roas: number | null;
  activeAds: number;
  activeCreatives: number;
}

export interface MetaDailyPoint {
  date: string;
  spend: number;
  impressions: number;
  linkClicks: number;
  purchases: number;
  purchaseValue: number;
  roas: number | null;
  ctr: number | null;
}

export interface MetaOverview {
  from: string;
  to: string;
  totals: MetaOverviewTotals;
  daily: MetaDailyPoint[];
}

export interface MetaCreativeStat {
  creativeId: string;
  name: string;
  title: string | null;
  format: string;
  thumbnailUrl: string | null;
  hasThumbnail: boolean;
  cta: string | null;
  adCount: number;
  spend: number;
  impressions: number;
  clicks: number;
  linkClicks: number;
  purchases: number;
  purchaseValue: number;
  roas: number | null;
  ctr: number | null;
  linkCtr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpa: number | null;
  frequency: number | null;
  hookRate: number | null;
  holdRate: number | null;
}

export interface MetaCreativeDetail {
  range: MetaRange;
  creative: {
    id: string;
    name: string;
    title?: string;
    body?: string;
    cta?: string;
    linkUrl?: string;
    format: string;
    thumbnailUrl?: string;
    imageUrl?: string;
    hasThumbnail?: boolean;
    bodies?: string[];
    titles?: string[];
  } | null;
  ads: { id: string; name: string; status: string; adsetName: string | null }[];
  daily: MetaDailyPoint[];
}

export interface MetaAnalysisInputCreative {
  creativeId: string;
  name: string;
  title: string | null;
  group: 'winner' | 'loser';
  metrics: { spend: number; roas: number | null; linkCtr: number | null };
}

export interface MetaAnalysisPattern {
  pattern: string;
  evidence: string;
  affectedCreativeIds: string[];
}

export interface MetaAnalysisPayload {
  summary: string;
  winningPatterns: MetaAnalysisPattern[];
  losingPatterns: MetaAnalysisPattern[];
  fatigueAlerts: { creativeId: string; reason: string }[];
  recommendations: string[];
  inputCreatives: MetaAnalysisInputCreative[];
  imagesAnalyzed: number;
}

export interface MetaAiReport<P> {
  kind: string;
  params: Record<string, unknown>;
  generatedAt: string;
  model: string | null;
  status: 'ok' | 'unavailable';
  statusReason: string | null;
  payload: P | null;
}

export function fetchMetaAnalysis(
  range: MetaRange,
  signal?: AbortSignal,
): Promise<{ range: MetaRange; report: MetaAiReport<MetaAnalysisPayload> | null }> {
  return apiFetch(`/api/meta/analysis?from=${range.from}&to=${range.to}`, { signal });
}

export function generateMetaAnalysis(range: MetaRange): Promise<{ range: MetaRange; report: MetaAiReport<MetaAnalysisPayload> }> {
  return apiFetch('/api/meta/analysis', { method: 'POST', body: range });
}

export interface MetaCopyVariant {
  title: string;
  body: string;
  cta: string;
  rationale: string;
  inspiredBy: string[];
}

export interface MetaCopyPayload {
  brief: string;
  variants: MetaCopyVariant[];
  corpusSize: number;
}

export function fetchMetaCopyBatches(signal?: AbortSignal): Promise<{ batches: MetaAiReport<MetaCopyPayload>[] }> {
  return apiFetch('/api/meta/copy', { signal });
}

export function generateMetaCopy(input: {
  brief: string;
  count?: number;
}): Promise<{ report: MetaAiReport<MetaCopyPayload> }> {
  return apiFetch('/api/meta/copy', { method: 'POST', body: input });
}

export interface MetaMonthlyPoint {
  month: string;
  value: number;
}

export interface MetaForecastPoint extends MetaMonthlyPoint {
  low: number;
  high: number;
}

export interface MetaSeriesForecast {
  history: MetaMonthlyPoint[];
  forecast: MetaForecastPoint[];
  seasonal: boolean;
  backtestMapePct: number | null;
}

export interface MetaForecastResponse {
  forecast: {
    monthsUsed: number;
    cpm: MetaSeriesForecast | null;
    cpc: MetaSeriesForecast | null;
    spend: MetaSeriesForecast | null;
  };
  commentary: MetaAiReport<{ commentary: string; recommendations: string[] }> | null;
}

export function fetchMetaForecast(signal?: AbortSignal): Promise<MetaForecastResponse> {
  return apiFetch('/api/meta/forecast', { signal });
}

export function generateMetaForecastCommentary(): Promise<{ commentary: MetaAiReport<{ commentary: string; recommendations: string[] }> }> {
  return apiFetch('/api/meta/forecast/commentary', { method: 'POST' });
}

export interface MetaDriveFile {
  id: string;
  name: string;
  path: string;
  mimeType: string;
  kind: 'image' | 'video';
  sizeBytes: number | null;
  thumbnailLink: string | null;
  imported: boolean;
}

export interface MetaDraftCreativeSpec {
  driveFileId?: string;
  existingCreativeId?: string;
  title: string;
  body: string;
  cta: string;
  linkUrl: string;
}

export interface MetaDraftAdsetSpec {
  name: string;
  dailyBudgetPln: number;
  targetingPreset: string;
  ads: { name: string; creative: MetaDraftCreativeSpec }[];
}

export interface MetaCampaignSpec {
  name: string;
  objective: 'OUTCOME_SALES' | 'OUTCOME_TRAFFIC' | 'OUTCOME_AWARENESS';
  adsets: MetaDraftAdsetSpec[];
}

export interface MetaCampaignDraft {
  _id: string;
  createdBy: string;
  status: 'draft' | 'approved' | 'published' | 'failed';
  spec: MetaCampaignSpec;
  aiRationale: string | null;
  publishedIds: { campaignId: string | null; adsetIds: string[]; adIds: string[] } | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export function fetchMetaConfig(signal?: AbortSignal): Promise<{
  config: { driveFolderId: string | null };
  targetingPresets: Record<string, { label: string }>;
}> {
  return apiFetch('/api/meta/config', { signal });
}

export function updateMetaConfig(driveFolderId: string | null): Promise<{ ok: true }> {
  return apiFetch('/api/meta/config', { method: 'PUT', body: { driveFolderId } });
}

export function fetchMetaDriveFiles(signal?: AbortSignal): Promise<{ folderId: string | null; files: MetaDriveFile[] }> {
  return apiFetch('/api/meta/drive/files', { signal });
}

export function fetchMetaDrafts(signal?: AbortSignal): Promise<{ drafts: MetaCampaignDraft[] }> {
  return apiFetch('/api/meta/drafts', { signal });
}

export function suggestMetaDraft(brief: string): Promise<{ draft: MetaCampaignDraft }> {
  return apiFetch('/api/meta/drafts/suggest', { method: 'POST', body: { brief } });
}

export function updateMetaDraft(id: string, spec: MetaCampaignSpec): Promise<{ ok: true }> {
  return apiFetch(`/api/meta/drafts/${id}`, { method: 'PUT', body: { spec } });
}

export function approveMetaDraft(id: string): Promise<{ ok: true }> {
  return apiFetch(`/api/meta/drafts/${id}/approve`, { method: 'POST' });
}

export function publishMetaDraft(id: string): Promise<{
  result: { status: 'published' | 'failed'; campaignId: string | null; error: string | null };
}> {
  return apiFetch(`/api/meta/drafts/${id}/publish`, { method: 'POST' });
}

export function deleteMetaDraft(id: string): Promise<{ ok: true }> {
  return apiFetch(`/api/meta/drafts/${id}`, { method: 'DELETE' });
}

export function fetchMetaOverview(range: MetaRange, signal?: AbortSignal): Promise<{ overview: MetaOverview }> {
  return apiFetch(`/api/meta/overview?from=${range.from}&to=${range.to}`, { signal });
}

export function fetchMetaCreatives(
  range: MetaRange,
  signal?: AbortSignal,
): Promise<{ range: MetaRange; creatives: MetaCreativeStat[] }> {
  return apiFetch(`/api/meta/creatives?from=${range.from}&to=${range.to}`, { signal });
}

export function fetchMetaCreativeDetail(
  id: string,
  range: MetaRange,
  signal?: AbortSignal,
): Promise<MetaCreativeDetail> {
  return apiFetch(`/api/meta/creatives/${id}?from=${range.from}&to=${range.to}`, { signal });
}

// Same-origin proxy serving the pipeline-cached thumbnail bytes. Used as an
// <img> src, so the browser carries the session cookie automatically.
export function metaThumbnailUrl(creativeId: string): string {
  return `/api/meta/creatives/${encodeURIComponent(creativeId)}/thumbnail`;
}
